/* Hover media is intentionally source-less until a shopper interacts. */
(() => {
  if (customElements.get('product-card-loop')) return;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const pointer = matchMedia('(hover: hover) and (pointer: fine)');
  const active = new Set();
  const allowed = () => !motion.matches && pointer.matches && !navigator.connection?.saveData && !document.hidden;
  const stopAll = () => [...active].forEach(loop => loop.stop());
  motion.addEventListener('change', stopAll);
  pointer.addEventListener('change', stopAll);
  document.addEventListener('visibilitychange', () => { if (document.hidden) stopAll(); });
  window.addEventListener('pagehide', stopAll);

  customElements.define('product-card-loop', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video || this.host) return;
      this.host = this.closest('.product-image-wrapper') || this;
      this.serial = 0;
      this.enter = event => { if (event.pointerType === 'mouse') this.play(); };
      this.leave = () => this.stop();
      this.focus = () => this.play();
      this.blur = event => { if (!this.host.contains(event.relatedTarget)) this.stop(); };
      this.error = () => this.stop();
      this.host.addEventListener('pointerenter', this.enter);
      this.host.addEventListener('pointerleave', this.leave);
      this.host.addEventListener('focusin', this.focus);
      this.host.addEventListener('focusout', this.blur);
      this.video.addEventListener('error', this.error);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (!entries[0].isIntersecting) this.stop();
        });
        this.observer.observe(this.host);
      }
    }

    async play() {
      if (!allowed() || !this.isConnected || active.has(this)) return;
      stopAll();
      active.add(this);
      const serial = ++this.serial;
      const video = this.video;
      video.muted = true;
      if (!video.getAttribute('src')) video.src = video.dataset.src;
      const reveal = () => {
        if (serial === this.serial && active.has(this) && this.isConnected && allowed()) {
          this.classList.add('is-playing');
        }
      };
      try {
        await video.play();
        if (serial !== this.serial) return;
        if ('requestVideoFrameCallback' in video) this.frame = video.requestVideoFrameCallback(reveal);
        else reveal();
      } catch {
        if (serial === this.serial) this.stop();
      }
    }

    stop() {
      ++this.serial;
      active.delete(this);
      this.classList.remove('is-playing');
      if (!this.video) return;
      if (this.frame !== undefined) this.video.cancelVideoFrameCallback?.(this.frame);
      this.frame = undefined;
      this.video.pause();
      try { this.video.currentTime = 0; } catch { /* Metadata may not have arrived. */ }
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      if (!this.host) return;
      this.host.removeEventListener('pointerenter', this.enter);
      this.host.removeEventListener('pointerleave', this.leave);
      this.host.removeEventListener('focusin', this.focus);
      this.host.removeEventListener('focusout', this.blur);
      this.video.removeEventListener('error', this.error);
      this.video.removeAttribute('src');
      this.video.load();
      this.host = null;
    }
  });
})();
